/**
 * Pick the single announcement to show on app open: the first one in the
 * server-ordered feed that this device has not yet seen. The server is the ordering
 * authority (priority desc, publish time desc, id — see announcements.helper.js), so
 * we do NOT re-sort here (a second comparator would risk diverging from the server,
 * e.g. the client payload omits `createdAt`). We still re-check time bounds locally as
 * defense-in-depth against stale cache / clock skew, and skip messages that render to
 * nothing. See specs/in-app-announcements/spec.md (AC-1.2, AC-3.5, AC-3.6).
 */

import type { Announcement } from '../types/announcements.types';
import { normalizeBlocks } from './announcementBlocks.helper';

/**
 * @param feed announcements from the server, already eligible + sorted (re-validated here)
 * @param seenIds ids already shown on this device
 * @returns the announcement to display, or `null` if none qualify
 */
export function selectAnnouncementToShow(
  feed: Announcement[] | null | undefined,
  seenIds: Iterable<string>,
  now: Date = new Date(),
): Announcement | null {
  if (!Array.isArray(feed) || feed.length === 0) return null;
  const seen = seenIds instanceof Set ? seenIds : new Set(seenIds);
  const nowMs = now.getTime();

  return (
    feed.find((a) => {
      if (!a || seen.has(a.id)) return false;
      if (a.publishedAt && new Date(a.publishedAt).getTime() > nowMs) return false;
      if (a.expiresAt && new Date(a.expiresAt).getTime() <= nowMs) return false;
      // A message whose blocks all normalize away has nothing to show — skip it
      // (and, importantly, do not consume its id as "seen").
      return normalizeBlocks(a.blocks).length > 0;
    }) ?? null
  );
}
